import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { riskApi } from '../../utils/riskApi';
import { formatStampShort } from '../../utils/datetime';
import TakeBaselineModal from './TakeBaselineModal';
import {
  ControlPill, ControlTiles, CoverageBar, CoverageCard, FoldedMetrics, Heatmap, LevelLegend, LevelPill, LowerHelp, ResultsBar, ScoreBlock, SettingsLine, Tip,
} from './riskUi';

const PAGE = 25;
const REL_POLL_MS = 3000;

// Summary / Detail is a per-viewer convenience, remembered per panel; storage may be blocked.
const viewKey = (id) => `risk.register.view.${id}`;
function readView(id) {
  try { return localStorage.getItem(viewKey(id)) === 'detail' ? 'detail' : 'summary'; } catch { return 'summary'; }
}
// Folded = only the headline metrics; also a per-viewer convenience, remembered per panel.
const foldKey = (id) => `risk.card.folded.${id}`;
function readFolded(id) {
  try { return localStorage.getItem(foldKey(id)) === '1'; } catch { return false; }
}

/**
 * One risk file (mockup screen 1): the product risk score, the before/after risk matrix, what
 * would lower the score most, the control-status tiles and the register.
 *
 * The register is filtered and paged ON THE SERVER (Critical Note #38) — the tiles, the
 * heatmap cells and the search box all just set query parameters.
 */
export default function RiskPanelCard({ panel, projectName, canManage, onEdit, onDelete, onMoveUp, onMoveDown, reloadKey, extraActions }) {
  const navigate = useNavigate();
  const [moreInfo, setMoreInfo] = useState(false);
  const [sum, setSum] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [after, setAfter] = useState(true);
  const [control, setControl] = useState('');
  const [cell, setCell] = useState('');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState(null);
  const [trend, setTrend] = useState([]);
  const [taking, setTaking] = useState(false);
  const [view, setView] = useState(() => readView(panel.id));
  const [rel, setRel] = useState(null);
  const [folded, setFolded] = useState(() => readFolded(panel.id));
  const token = useRef(0);

  const loadSummary = useCallback(async (opts = {}) => {
    try {
      setError('');
      const s = await riskApi.summary(panel.id, opts);
      setSum(s);
      setRel(s.relations || null);
      return s;
    } catch (e) {
      setError(e.message);
      return null;
    }
  }, [panel.id]);

  useEffect(() => { setSum(null); loadSummary(); }, [loadSummary, reloadKey]);

  // The relation walk (mitigation → link type → tests) runs in the background on the server.
  // While it runs, poll its progress — a DB read, never Orcanos — and re-read the card once it
  // ends. Keyed on the state string only: App re-renders every second (Critical Note #59).
  const relState = rel?.state || '';
  useEffect(() => {
    if (relState !== 'running') return undefined;
    let live = true;
    const t = setInterval(async () => {
      try {
        const st = await riskApi.relations(panel.id);
        if (!live) return;
        if (st.state === 'running') setRel(st);
        else { clearInterval(t); await loadSummary(); }
      } catch { /* keep the last state; the next tick retries */ }
    }, REL_POLL_MS);
    return () => { live = false; clearInterval(t); };
  }, [relState, panel.id, loadSummary]);

  const loadTrend = useCallback(() => {
    riskApi.trend(panel.id).then((d) => setTrend(d.points || [])).catch(() => setTrend([]));
  }, [panel.id]);
  useEffect(() => { loadTrend(); }, [loadTrend, reloadKey]);

  // Register page — re-read whenever a filter changes (and after the summary rebuilt).
  useEffect(() => {
    if (!sum || folded) return undefined;
    const mine = ++token.current;
    const params = { page, limit: PAGE, search: query, control, cell };
    if (view === 'detail') params.detail = 1;
    riskApi.rows(panel.id, params)
      .then((d) => { if (mine === token.current) setRows(d); })
      .catch((e) => { if (mine === token.current) setError(e.message); });
    return undefined;
  }, [panel.id, sum, page, query, control, cell, view, folded]);

  const fold = (f) => {
    setFolded(f);
    try { localStorage.setItem(foldKey(panel.id), f ? '1' : '0'); } catch { /* per-viewer convenience only */ }
  };

  const pickView = (v) => {
    setView(v);
    try { localStorage.setItem(viewKey(panel.id), v); } catch { /* per-viewer convenience only */ }
  };

  // Debounce the search box.
  useEffect(() => {
    const t = setTimeout(() => { setQuery(search.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const refresh = async (kind) => {
    setBusy(kind);
    if (kind === 'settings') {
      try { await riskApi.refreshSettings(panel.id); } catch (e) { setError(e.message); }
      await loadSummary();
    } else {
      await loadSummary({ refresh: 1 });
    }
    loadTrend();          // the score sparkline too — Refresh means the whole card
    setBusy('');
  };

  const pickControl = (st) => { setControl(st); setCell(''); setPage(1); };
  const pickCell = (c) => {
    const v = `${after ? 'post' : 'pre'}:${c.p},${c.s}`;
    setCell((x) => (x === v ? '' : v)); setControl(''); setPage(1);
  };

  const s = sum?.summary;
  const heat = s ? (after ? s.heat_post : s.heat_pre) : null;
  const total = rows?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE));

  return (
    <section className={`rsk-card${folded ? ' rsk-card--folded' : ''}`} aria-label={panel.name}>
      <div className="rsk-card-head">
        <div style={{ minWidth: 0 }}>
          <div className="rsk-card-titlerow">
            <button type="button" className={`rsk-foldbtn${folded ? ' rsk-foldbtn--closed' : ''}`}
              onClick={() => fold(!folded)} aria-expanded={!folded}
              title={folded ? 'Unfold — show the full panel' : 'Fold — show only the main metrics'}
              aria-label={folded ? 'Unfold panel' : 'Fold panel'}>▼</button>
            {canManage && (onMoveUp || onMoveDown) && (
              <span className="rsk-move" role="group" aria-label="Reorder panel">
                <button type="button" onClick={onMoveUp || undefined} disabled={!onMoveUp}
                  title="Move panel up" aria-label="Move panel up">↑</button>
                <button type="button" onClick={onMoveDown || undefined} disabled={!onMoveDown}
                  title="Move panel down" aria-label="Move panel down">↓</button>
              </span>
            )}
            <h2 className="rsk-card-title">{panel.name}</h2>
            <WarningFlag list={sum?.warnings} />
          </div>
          <div className="rsk-card-meta">
            {projectName ? <>Project: {projectName} · </> : null}
            Filter: {panel.filter_name || `#${panel.filter_id}`}
            {' '}
            <button type="button" className="drv-link rsk-small-link" onClick={() => setMoreInfo((m) => !m)}
              aria-expanded={moreInfo}>{moreInfo ? 'Less' : 'More'}</button>
          </div>
          {moreInfo && (
            <div className="rsk-card-meta">
              {s ? <>{s.n} risks</> : null}
              {sum?.built_at ? <>{s ? ' · ' : ''}cached {formatStampShort(sum.built_at)}</> : null}
              {sum?.settings ? <> · <SettingsLine settings={sum.settings} busy={busy === 'settings'} onRefresh={() => refresh('settings')} /></> : null}
            </div>
          )}
          {/* A walk still running or failed stays visible — it explains numbers that are about to change. */}
          {(moreInfo || ['running', 'failed', 'interrupted'].includes(rel?.state)) && <RelationsLine rel={rel} />}
        </div>
        <div className="rsk-actions">
          {extraActions}
          <button type="button" className="drv-btn drv-btn--primary" onClick={() => navigate(`/risk/${panel.id}/studio`)}>Risk Studio</button>
          <button type="button" className="rsk-iconbtn" onClick={() => refresh('data')} disabled={!!busy}
            title={busy === 'data' ? 'Reading Orcanos…'
              : (sum?.built_at ? `Cached ${formatStampShort(sum.built_at)} — click to refresh from Orcanos` : 'Refresh from Orcanos')}
            aria-label="Refresh from Orcanos">
            <span className={busy === 'data' ? 'rsk-spin' : undefined}>⟳</span>
          </button>
          {canManage && panel.can_edit && (
            <>
              <button type="button" className="rsk-iconbtn" onClick={onEdit} title="Edit" aria-label="Edit">✎</button>
              <button type="button" className="rsk-iconbtn rsk-iconbtn--danger" onClick={onDelete} title="Delete" aria-label="Delete">🗑</button>
            </>
          )}
          <MoreMenu items={[
            canManage && { label: 'Import Excel', onClick: () => navigate(`/risk/${panel.id}/import`) },
            canManage && { label: 'Take baseline', onClick: () => setTaking(true), disabled: !sum },
            { label: `Baselines${trend.length ? ` · ${trend.length}` : ''}`, onClick: () => navigate(`/risk/${panel.id}/baselines`) },
            { label: 'Reports', onClick: () => navigate(`/risk/${panel.id}/reports`) },
          ].filter(Boolean)} />
        </div>
      </div>

      {error && <div className="drv-error" role="alert" style={{ margin: '12px 18px 0' }}>{error}</div>}
      {sum?.missing_columns?.length > 0 && (
        <div className="rsk-fine" style={{ margin: '10px 18px 0' }}>
          Not in the filter: {sum.missing_columns.join(', ')}. Add them in Orcanos for a fuller picture.
        </div>
      )}

      {!sum && !error && <div className="drv-muted" style={{ padding: 18 }}>Reading the risk file from Orcanos…</div>}

      {s && folded && (
        <FoldedMetrics summary={s} onPickControl={(st) => { pickControl(st); fold(false); }} />
      )}

      {s && !folded && (
        <>
          <div className="rsk-grid3">
            <div className="rsk-cell"><ScoreBlock score={s.score} trend={trend} /></div>

            <div className="rsk-cell">
              <div className="rsk-row-between">
                <div className="rsk-lbl">Risk matrix</div>
                <div className="rsk-seg" role="group" aria-label="Before or after mitigation">
                  <button type="button" className={!after ? 'on' : ''} onClick={() => { setAfter(false); setCell(''); }}>Before</button>
                  <button type="button" className={after ? 'on' : ''} onClick={() => { setAfter(true); setCell(''); }}>After mitigation</button>
                </div>
              </div>
              <Heatmap heat={heat} onCell={pickCell} />
              <LevelLegend levels={after ? s.levels_post : s.levels_pre} />
            </div>

            <div className="rsk-cell">
              <div className="rsk-lbl rsk-score-head"><span>What would raise the score most</span><LowerHelp /></div>
              {(s.score?.lower || []).length === 0 && <div className="rsk-dim">Nothing — every risk is at its lowest contribution.</div>}
              {(s.score?.lower || []).map((l) => (
                <button type="button" key={l.key} className="rsk-lower"
                  onClick={() => navigate(`/risk/${panel.id}/r/${l.item_id}`)}>
                  <span className="rsk-lower-key">{l.key}</span>
                  <span className="rsk-lower-name">{l.name}<span>{l.why}</span></span>
                  <span className="rsk-lower-pts">+{l.points}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="rsk-band">
            <div className="rsk-lbl">
              Control status — risk → mitigation → test → last run
              {!sum.verification && !(sum.results_from || []).length && <span className="rsk-lbl-note"> · no test filter set, so test results are not read</span>}
              {!sum.verification && (sum.results_from || []).length > 0 && <span className="rsk-lbl-note"> · results from Test Run filter #{sum.results_from.join(', #')}</span>}
            </div>
            <div className="rsk-band-row">
              <ControlTiles counts={s.control} active={control} onPick={pickControl} />
              <CoverageCard coverage={s.coverage} verification={sum.verification} running={relState === 'running'} />
            </div>
          </div>

          <div className="rsk-reg-bar">
            <div className="rsk-lbl">
              Register
              {(control || cell) && (
                <button type="button" className="drv-link rsk-small-link" onClick={() => { setControl(''); setCell(''); setPage(1); }}>
                  clear filter ({control ? control.replace('_', ' ') : `cell ${cell.replace('post:', 'after ').replace('pre:', 'before ')}`})
                </button>
              )}
            </div>
            <div className="rsk-reg-tools">
            <div className="rsk-seg" role="group" aria-label="Register view">
              <button type="button" className={view === 'summary' ? 'on' : ''} aria-pressed={view === 'summary'}
                onClick={() => pickView('summary')}>Summary</button>
              <button type="button" className={view === 'detail' ? 'on' : ''} aria-pressed={view === 'detail'}
                onClick={() => pickView('detail')}>Detail</button>
            </div>
            <input type="search" className="drv-input rsk-search" aria-label="Search risks" placeholder="Search risks, hazards, mitigations"
              value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
          </div>
          {view === 'detail' ? (
            <DetailTable rows={rows} onOpen={(r) => navigate(`/risk/${panel.id}/r/${r.item_id}`)} />
          ) : (
          <div className="rsk-table-wrap rsk-table-wrap--reg">
            <table className="rsk-table">
              <thead>
                <tr>
                  <th>Key / Risk</th><th>Hazardous situation</th><th>P · S before</th><th>P · S after</th>
                  <th>Level before → after</th><th>Control</th><th>Mitigations</th>
                  <th>Verification</th><th>Test results</th>
                </tr>
              </thead>
              <tbody>
                {(rows?.rows || []).map((r) => (
                  <tr key={r.key} className="rsk-tr" onClick={() => navigate(`/risk/${panel.id}/r/${r.item_id}`)}>
                    <td>
                      {/* The key opens the risk in Orcanos; the rest of the row opens our detail page. */}
                      {r.url
                        ? <Tip as="a" className="rsk-key rsk-key--link" href={r.url} target="_blank" rel="noopener noreferrer"
                            tip="Open in Orcanos (new tab)" onClick={(e) => e.stopPropagation()}>{r.key} ↗</Tip>
                        : <span className="rsk-key">{r.key}</span>}
                      {r.anomalies?.length > 0 && <AnomalyFlag list={r.anomalies} />}
                      <div className="rsk-name">{r.name}</div>
                    </td>
                    <td className="rsk-sub">{r.situation || r.hazard || r.failure_mode || '—'}</td>
                    <td>{ps(r.p_pre, r.s_pre)}</td>
                    <td>{ps(r.p_post, r.s_post)}</td>
                    <td className="rsk-nowrap"><LevelPill level={r.level_pre} /> → <LevelPill level={r.level_post} /></td>
                    <td><ControlPill control={r.control} /></td>
                    <td className="rsk-sub">{r.mitigation_count ? `${r.mitigation_count} req.` : '—'}</td>
                    <td><CoverageBar coverage={r.coverage} /></td>
                    <td><ResultsBar results={r.results} /></td>
                  </tr>
                ))}
                {rows && rows.rows.length === 0 && (
                  <tr><td colSpan={9} className="rsk-dim" style={{ textAlign: 'center', padding: 24 }}>No risks match.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          )}
          <div className="rsk-pager">
            <span>{total ? `${(page - 1) * PAGE + 1}–${Math.min(page * PAGE, total)} of ${total}` : '0 risks'}</span>
            <button type="button" className="drv-btn" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
            <button type="button" className="drv-btn" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</button>
          </div>
        </>
      )}
      {taking && (
        <TakeBaselineModal panel={panel} onClose={() => setTaking(false)}
          onTaken={() => { setTaking(false); loadTrend(); }} />
      )}
    </section>
  );
}

/** The card's ⋮ menu — the less frequent actions. Closes on pick, outside click or Escape. */
function MoreMenu({ items }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);
  return (
    <span className="rsk-more" ref={ref}>
      <button type="button" className="rsk-iconbtn" onClick={() => setOpen((o) => !o)}
        title="More actions" aria-label="More actions" aria-haspopup="menu" aria-expanded={open}>⋮</button>
      {open && (
        <div className="rsk-more-menu" role="menu">
          {items.map((it) => (
            <button key={it.label} type="button" role="menuitem" disabled={it.disabled}
              onClick={() => { setOpen(false); it.onClick(); }}>{it.label}</button>
          ))}
        </div>
      )}
    </span>
  );
}

/** The background relation walk, in one line: running (with progress), done, or why not. */
function RelationsLine({ rel }) {
  if (!rel || rel.state === 'none') return null;
  if (rel.state === 'running') {
    return (
      <div className="rsk-rel" role="status">
        <span className="rsk-rel-spin" aria-hidden="true" />
        Reading mitigation links and tests in the background{rel.total ? ` — ${rel.done} of ${rel.total}` : '…'}
        <span className="rsk-dim">· the card updates when it finishes</span>
      </div>
    );
  }
  if (rel.state === 'failed' || rel.state === 'interrupted') {
    return (
      <div className="rsk-rel rsk-rel--warn" role="status">
        {rel.state === 'failed' ? `Could not read the mitigation links: ${rel.error}` : 'Reading the mitigation links was interrupted.'}
        {rel.walked_at ? ` Showing the links read ${formatStampShort(rel.walked_at)}.` : ' Showing the links from the filter only.'}
        {' '}Refresh to try again.
      </div>
    );
  }
  if (!rel.total && !rel.reached) return null;
  return (
    <div className={`rsk-rel${rel.failed ? ' rsk-rel--warn' : ''}`}>
      Mitigation links and tests read {formatStampShort(rel.walked_at)}
      {rel.failed ? ` · ${rel.failed} could not be read (${rel.error})` : ''}
    </div>
  );
}

/** One cell per column the filter selects, in the filter's order — values as Orcanos sends them. */
function DetailTable({ rows, onOpen }) {
  // Until the detail page arrives the summary-shaped rows have no cells; draw nothing stale.
  if (!rows || !rows.columns) return <div className="drv-muted" style={{ padding: 18 }}>Loading all fields…</div>;
  const cols = rows.columns.filter((c) => c.name !== rows.key_column);
  return (
    <div className="rsk-table-wrap rsk-table-wrap--reg">
      <table className="rsk-table rsk-table--detail">
        <thead>
          <tr><th className="rsk-sticky">Key</th>{cols.map((c) => <th key={c.name}><Tip tip={c.name !== c.title ? `Orcanos field: ${c.name}` : null}>{c.title}</Tip></th>)}</tr>
        </thead>
        <tbody>
          {rows.rows.map((r) => (
            <tr key={r.key} className="rsk-tr" onClick={() => onOpen(r)}>
              <td className="rsk-sticky rsk-nowrap">
                {r.url
                  ? <Tip as="a" className="rsk-key rsk-key--link" href={r.url} target="_blank" rel="noopener noreferrer"
                      tip="Open in Orcanos (new tab)" onClick={(e) => e.stopPropagation()}>{r.key} ↗</Tip>
                  : <span className="rsk-key">{r.key}</span>}
                {r.anomalies?.length > 0 && <AnomalyFlag list={r.anomalies} />}
              </td>
              {cols.map((c) => {
                const v = r.cells?.[c.name] || '';
                return (
                  <td key={c.name}>
                    <Tip as="div" className="rsk-dcell" tip={v.length > 80 ? <span className="rsk-tip-long">{v}</span> : null}>{v || <span className="rsk-dim">—</span>}</Tip>
                  </td>
                );
              })}
            </tr>
          ))}
          {rows.rows.length === 0 && (
            <tr><td colSpan={cols.length + 1} className="rsk-dim" style={{ textAlign: 'center', padding: 24 }}>No risks match.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function ps(p, s) {
  if (!p && !s) return <span className="rsk-dim">—</span>;
  return <span className="rsk-ps">P{p || '?'} · S{s || '?'}</span>;
}

// One line per anomaly: a short title, then what it means. Shared by the flag's tooltip and
// the detail page's "Worth a look" note.
export function anomalyItems(list) {
  const when = (a) => (a.when === 'pre' ? 'Before mitigation' : 'After mitigation');
  return (list || []).map((a) => {
    if (a.kind === 'level_mismatch') {
      return {
        title: `${when(a)}: level differs from the FMEA table`,
        body: a.stored_label
          ? `Orcanos stores ${a.stored_label}; P × S gives ${a.computed_label}. Showing Orcanos’s value.`
          : 'Showing Orcanos’s stored value.',
      };
    }
    if (a.kind === 'post_above_pre') return { title: 'Level went up after mitigation', body: 'The level after mitigation is higher than before.' };
    if (a.kind === 'lowered_without_mitigation') return { title: 'Lowered with no mitigation', body: 'Probability drops after mitigation, but no mitigation is traced.' };
    return { title: a.kind, body: '' };
  });
}

// The panel's setup warnings (test filter, link types, settings) — an icon by the title, the
// text on hover/focus, so they don't take a banner's worth of space on every card.
function WarningFlag({ list }) {
  if (!list?.length) return null;
  return (
    <Tip className="rsk-warnflag" tabIndex={0} tipClassName="rsk-tip--wide"
      aria-label={`${list.length} setup warning${list.length === 1 ? '' : 's'}`}
      heading={list.length === 1 ? 'Setup warning' : `${list.length} setup warnings`}
      tip={<ul className="rsk-tip-list">{list.map((w) => <li key={w}><span>{w}</span></li>)}</ul>}>
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
        <path d="M12 3 2 20.5h20L12 3Z" fill="currentColor" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M12 9.5v5" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
        <circle cx="12" cy="17.3" r="1.25" fill="#fff" />
      </svg>
    </Tip>
  );
}

function AnomalyFlag({ list }) {
  if (!list?.length) return null;
  const items = anomalyItems(list);
  return (
    <Tip className="rsk-flag" tabIndex={0} aria-label={`${items.length} thing${items.length === 1 ? '' : 's'} worth a look`}
      heading="Worth a look"
      tip={(
        <ul className="rsk-tip-list">
          {items.map((a, i) => <li key={i}><strong>{a.title}</strong>{a.body && <span>{a.body}</span>}</li>)}
        </ul>
      )}>
      !
    </Tip>
  );
}
